import type { TaskCardProps } from "model/activitiesModel";
import type { UserData } from "hooks/useUserDataKeys";
import { getUserData, getUserSchema } from "common/utilities";
import { offerableKeys } from "./authorizedKeys";
import { selectsElement } from "./visibilityImpact";
import type { CardPreferences } from "./incardPreference";
import type { LegacySchemaRow } from "./legacyAdapter";

// Personal card preferences never make an unauthorized field visible, the
// per-element selection the server applies is never undone here, and the
// preference only filters among the fields the row already grants.
export function cardFields(
  task: TaskCardProps["task"] | null | undefined,
  preferences?: CardPreferences
): [string, UserData][] {
  if (!preferences) return [];
  const authorized = new Set(
    task?.customFieldDefinitions
      ? task.customFieldDefinitions.map((field) => field.legacyKey ?? field.label)
      : offerableKeys([getUserSchema(task?.userschema) as LegacySchemaRow[]])
  );

  // Il fieldId per chiave: per i campi di schema arriva dalla definizione,
  // per gli ad-hoc dalla entry stessa (`value.fieldId`). Serve a leggere
  // la preferenza personale dell'utente restituita dal server.
  const definitionByKey = new Map(
    (task?.customFieldDefinitions ?? []).map((field) => [
      field.legacyKey ?? field.label,
      field,
    ])
  );

  return Object.entries<UserData>(getUserData(task?.userdata)).filter(
    ([key, value]) => {
      if (!authorized.has(key)) return false;
      const definition = definitionByKey.get(key);
      // La riga mostra solo cio' che la sua visibilita' seleziona per
      // **questo** elemento (`task.designerid`): un campo di un altro task non
      // compare nemmeno con `inCard` e preferenza accesi.
      if (!selectsElement(definition?.visibility, task?.designerid)) return false;
      const fieldId = definition?.fieldId ?? value.fieldId;
      // Il default e' `definition.inCard` per i campi di schema, `value.inCard`
      // per gli ad-hoc; la preferenza personale dell'utente, se c'e', vince —
      // ma solo fra i campi che la riga gia' rende visibili.
      const fallback = definition?.inCard ?? value.inCard;
      return ((fieldId ? preferences[fieldId] : undefined) ?? fallback) === true;
    }
  );
}

